import React, { useState } from 'react';
import { useQuery, useMutation } from '@apollo/client';
import {
  Box, Typography, Grid, Card, CardContent, Button, Stack, Chip,
  Dialog, DialogTitle, DialogContent, DialogActions, TextField, MenuItem,
  Alert, IconButton, LinearProgress, Table, TableHead, TableRow,
  TableCell, } from '@mui/material';
import TableBody from '../components/PagedTableBody'; // pages rows beyond 10
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import PreviewIcon from '@mui/icons-material/Preview';
import {
  INVITATION_TEMPLATES_QUERY,
  CREATE_INVITATION_TEMPLATE_MUTATION, UPDATE_INVITATION_TEMPLATE_MUTATION, DELETE_INVITATION_TEMPLATE_MUTATION,
} from '../graphql/queries';
import ContractedVendorsSection from '../components/ContractedVendorsSection';

const STYLES = ['traditional','modern','floral','minimalist','royal','festive'];
const LANGS  = ['english','tamil','hindi','telugu','kannada','malayalam'];
const EVENTS = ['wedding','reception','mehendi','engagement','baby_shower','birthday'];
const EMPTY  = { name:'', style:'traditional', language:'english', eventType:'wedding', bodyHtml:'', subjectTemplate:'', thumbnailUrl:'', isSystem:false, isActive:true, notes:'' };

export default function AdminInvitationPage() {
  const [error, setError]     = useState('');
  const [dialog, setDialog]   = useState(false);
  const [preview, setPreview] = useState(null);
  const [editTmpl, setEditTmpl] = useState(null);
  const [form, setForm]         = useState(EMPTY);
  const [confirmDel, setConfirmDel] = useState(null);

  const { data, loading, refetch } = useQuery(INVITATION_TEMPLATES_QUERY, { fetchPolicy:'cache-and-network' });
  const templates = data?.invitationTemplates || [];

  const [createTmpl] = useMutation(CREATE_INVITATION_TEMPLATE_MUTATION, { onCompleted:()=>{ setDialog(false); setForm(EMPTY); setError(''); refetch(); }, onError:e=>setError(e.message) });
  const [updateTmpl] = useMutation(UPDATE_INVITATION_TEMPLATE_MUTATION, { onCompleted:()=>{ setDialog(false); setEditTmpl(null); setError(''); refetch(); }, onError:e=>setError(e.message) });
  const [deleteTmpl] = useMutation(DELETE_INVITATION_TEMPLATE_MUTATION, { onCompleted:()=>{ setConfirmDel(null); refetch(); } });

  const openEdit = (t) => {
    setEditTmpl(t);
    setForm({ name:t.name||'', style:t.style||'traditional', language:t.language||'english', eventType:t.eventType||'wedding', bodyHtml:t.bodyHtml||'', subjectTemplate:t.subjectTemplate||'', thumbnailUrl:t.thumbnailUrl||'', isSystem:t.isSystem||false, isActive:t.isActive!==false, notes:t.notes||'' });
    setDialog(true);
  };
  const handleSave = () => editTmpl ? updateTmpl({ variables:{ id:editTmpl.id, ...form } }) : createTmpl({ variables:form });

  return (
    <Box sx={{ p:3 }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={3}>
        <Box>
          <Typography variant="h5" fontWeight={700}>Admin — Invitation Templates</Typography>
          <Typography variant="body2" color="text.secondary">Manage invitation templates available to all users</Typography>
        </Box>
        <Button variant="contained" startIcon={<AddIcon/>} onClick={()=>{ setEditTmpl(null); setForm(EMPTY); setDialog(true); }}>New Template</Button>
      </Stack>

      <ContractedVendorsSection category="invitation" />
      {error && <Alert severity="error" sx={{mb:2}} onClose={()=>setError('')}>{error}</Alert>}
      {loading && <LinearProgress sx={{mb:2}}/>}

      <Table>
        <TableHead>
          <TableRow>
            <TableCell>Name</TableCell><TableCell>Style</TableCell><TableCell>Language</TableCell>
            <TableCell>Event Type</TableCell><TableCell>Status</TableCell><TableCell>Actions</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {templates.map(t=>(
            <TableRow key={t.id}>
              <TableCell>
                <Stack direction="row" alignItems="center" spacing={1}>
                  {t.thumbnailUrl && <img src={t.thumbnailUrl} alt={t.name} style={{width:32,height:32,objectFit:'cover',borderRadius:4}} onError={e=>e.target.style.display='none'}/>}
                  <Box>
                    <Typography fontWeight={600}>{t.name}</Typography>
                    {t.isSystem && <Chip size="small" label="System" color="secondary" sx={{fontSize:10}}/>}
                  </Box>
                </Stack>
              </TableCell>
              <TableCell>{t.style}</TableCell>
              <TableCell>{t.language}</TableCell>
              <TableCell>{t.eventType}</TableCell>
              <TableCell><Chip size="small" label={t.isActive?'Active':'Inactive'} color={t.isActive?'success':'default'}/></TableCell>
              <TableCell>
                <IconButton size="small" onClick={()=>setPreview(t)}><PreviewIcon fontSize="small"/></IconButton>
                <IconButton size="small" onClick={()=>openEdit(t)}><EditIcon fontSize="small"/></IconButton>
                <IconButton size="small" color="error" onClick={()=>setConfirmDel(t.id)} disabled={t.isSystem}><DeleteIcon fontSize="small"/></IconButton>
              </TableCell>
            </TableRow>
          ))}
          {templates.length===0 && !loading && <TableRow><TableCell colSpan={6}><Typography textAlign="center" color="text.secondary" py={3}>No templates yet.</Typography></TableCell></TableRow>}
        </TableBody>
      </Table>

      {/* Create/Edit Dialog */}
      <Dialog open={dialog} onClose={()=>setDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>{editTmpl?'Edit Template':'New Template'}</DialogTitle>
        <DialogContent sx={{display:'flex',flexDirection:'column',gap:2,pt:2}}>
          <TextField label="Template Name *" value={form.name} onChange={e=>setForm(f=>({...f,name:e.target.value}))} fullWidth/>
          <Grid container spacing={1.5}>
            <Grid item xs={4}><TextField select label="Style" value={form.style} onChange={e=>setForm(f=>({...f,style:e.target.value}))} fullWidth>{STYLES.map(s=><MenuItem key={s} value={s}>{s}</MenuItem>)}</TextField></Grid>
            <Grid item xs={4}><TextField select label="Language" value={form.language} onChange={e=>setForm(f=>({...f,language:e.target.value}))} fullWidth>{LANGS.map(l=><MenuItem key={l} value={l}>{l}</MenuItem>)}</TextField></Grid>
            <Grid item xs={4}><TextField select label="Event Type" value={form.eventType} onChange={e=>setForm(f=>({...f,eventType:e.target.value}))} fullWidth>{EVENTS.map(ev=><MenuItem key={ev} value={ev}>{ev.replace('_',' ')}</MenuItem>)}</TextField></Grid>
          </Grid>
          <TextField label="Subject Template" value={form.subjectTemplate} onChange={e=>setForm(f=>({...f,subjectTemplate:e.target.value}))} fullWidth placeholder="You're invited to {{bride}}'s & {{groom}}'s Wedding!"/>
          <TextField label="Body HTML / Content *" value={form.bodyHtml} onChange={e=>setForm(f=>({...f,bodyHtml:e.target.value}))} multiline rows={8} fullWidth placeholder="<p>Dear {{guest_name}},...</p>"/>
          <TextField label="Thumbnail URL" value={form.thumbnailUrl} onChange={e=>setForm(f=>({...f,thumbnailUrl:e.target.value}))} fullWidth/>
          <TextField label="Notes" value={form.notes} onChange={e=>setForm(f=>({...f,notes:e.target.value}))} multiline rows={2} fullWidth/>
          <Stack direction="row" spacing={2}>
            <Button variant={form.isActive?'contained':'outlined'} size="small" color="success" onClick={()=>setForm(f=>({...f,isActive:true}))}>Active</Button>
            <Button variant={!form.isActive?'contained':'outlined'} size="small" color="inherit" onClick={()=>setForm(f=>({...f,isActive:false}))}>Inactive</Button>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={()=>setDialog(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleSave} disabled={!form.name||!form.bodyHtml}>Save</Button>
        </DialogActions>
      </Dialog>

      {/* Preview Dialog */}
      <Dialog open={!!preview} onClose={()=>setPreview(null)} maxWidth="md" fullWidth>
        <DialogTitle>{preview?.name} — Preview</DialogTitle>
        <DialogContent>
          {preview?.bodyHtml
            ? <Box dangerouslySetInnerHTML={{ __html: preview.bodyHtml }} sx={{border:'1px solid',borderColor:'divider',borderRadius:1,p:2,minHeight:200}}/>
            : <Typography color="text.secondary">No body content.</Typography>
          }
        </DialogContent>
        <DialogActions><Button onClick={()=>setPreview(null)}>Close</Button></DialogActions>
      </Dialog>

      {/* Confirm Delete */}
      <Dialog open={!!confirmDel} onClose={()=>setConfirmDel(null)}>
        <DialogTitle>Delete Template?</DialogTitle>
        <DialogContent><Typography>This template will be permanently deleted.</Typography></DialogContent>
        <DialogActions>
          <Button onClick={()=>setConfirmDel(null)}>Cancel</Button>
          <Button variant="contained" color="error" onClick={()=>deleteTmpl({variables:{id:confirmDel}})}>Delete</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
